import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { ApiError, api } from "@/lib/api";
import { workspaceChatStatus, workspaceCodeHostStatus, type ConnectionStatus } from "@/lib/connection-status";
import { useFeatureFlag } from "@/lib/feature-flag";
import { useOrgRole } from "@/lib/use-org-role";
import {
  ConnectionHeader,
  ConnectionList,
  ConnectionRow,
  ConnectionSection,
  DetailList,
  DetailRow,
  Notice,
  StatusLine,
} from "@/pages/container-tabs/connections/ConnectionParts";
import {
  CONNECTION_GROUPS,
  CONNECTIONS,
  findConnection,
  type ConnectionDef,
  type ConnectionId,
} from "@/pages/container-tabs/connections/connections-lib";
import { BOO_APPS, type BooAppStatus, type ChatPlatform } from "@/pages/container-tabs/connections/use-boo-app";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { WorkspaceCodeHostsSection } from "./WorkspaceCodeHosts";
import { WorkspaceSlack } from "./WorkspaceSlack";

// The org's Connections page: the workspace's side of Connections, like Devin's
// Enterprise Settings → Connections plus Repository permissions. Shared
// connections are made once here (or from a Boo) and then given to Boos:
// a chat app goes to one Boo at a time; a code host connection to any number,
// chosen per connection. A Boo's own Connections tab shows what it was given
// and what it connected for itself.

const CHAT_PLATFORMS: Partial<Record<ConnectionId, ChatPlatform>> = {
  slack: "slack",
  "microsoft-teams": "teams",
  "google-chat": "gchat",
  discord: "discord",
};

// Same key and fetcher as a Boo's chat connection page, so they share a cache.
function useChatInstall(platform: ChatPlatform, enabled = true) {
  return useQuery({
    queryKey: [`${platform}-boo-status`],
    queryFn: (): Promise<BooAppStatus> => api[platform].boo.status(),
    enabled,
  });
}

// Where each connection stands for the workspace. Microsoft 365 and Google
// Workspace are per Boo only, so they aren't listed here.
function useWorkspaceStatuses(): Partial<Record<ConnectionId, ConnectionStatus>> {
  const gchatBooApp = useFeatureFlag("gchat-boo-app");
  const discordEnabled = useFeatureFlag("discord-integration");
  const installs = {
    slack: useChatInstall("slack"),
    teams: useChatInstall("teams"),
    gchat: useChatInstall("gchat", gchatBooApp),
    discord: useChatInstall("discord", discordEnabled),
  };
  const hosts = useQuery({ queryKey: ["code-hosts", "workspace"], queryFn: () => api.codeHosts.list() });

  const chat = (platform: ChatPlatform) => {
    const { data, isLoading } = installs[platform];
    return workspaceChatStatus(data, isLoading, data && BOO_APPS[platform].place(data));
  };
  const out: Partial<Record<ConnectionId, ConnectionStatus>> = {
    slack: chat("slack"),
    "microsoft-teams": chat("teams"),
  };
  if (gchatBooApp) out["google-chat"] = chat("gchat");
  if (discordEnabled) out.discord = chat("discord");
  // Members who can't manage the workspace pool get a 403; code hosts stay off
  // their list.
  if (!(hosts.error instanceof ApiError && hosts.error.status === 403)) {
    for (const host of ["github", "gitlab"] as const) {
      out[host] = workspaceCodeHostStatus(
        (hosts.data?.workspace ?? []).filter((c) => c.host === host && c.status === "active"),
        hosts.isLoading,
      );
    }
  }
  return out;
}

export function OrgConnectionsTab() {
  const { orgId } = useOrgRole();
  const statuses = useWorkspaceStatuses();
  if (!orgId) return null;
  return (
    <div className="flex max-w-3xl flex-col gap-8">
      {CONNECTION_GROUPS.map((group) => {
        const defs = CONNECTIONS.filter((c) => c.group === group.id && statuses[c.id]);
        if (defs.length === 0) return null;
        return (
          <ConnectionSection key={group.id} title={group.title} description={WORKSPACE_GROUP_COPY[group.id]}>
            <ConnectionList>
              {defs.map((def) => (
                <ConnectionRow
                  key={def.id}
                  def={def}
                  status={statuses[def.id]}
                  to="/orgs/$orgId/connections/$connection"
                  params={{ orgId, connection: def.id }}
                />
              ))}
            </ConnectionList>
          </ConnectionSection>
        );
      })}
    </div>
  );
}

const WORKSPACE_GROUP_COPY: Record<ConnectionDef["group"], string> = {
  chat: "Chat apps installed in your workspace. Each one talks through one Boo at a time.",
  code: "GitHub organizations and GitLab groups your Boos can be given.",
  work: "",
};

export function OrgConnectionPage() {
  const { orgId } = useOrgRole();
  const params = useParams({ strict: false }) as { connection?: string };
  const def = findConnection(params.connection);
  const statuses = useWorkspaceStatuses();
  if (!orgId) return null;
  const status = def ? statuses[def.id] : undefined;
  const platform = def ? CHAT_PLATFORMS[def.id] : undefined;

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <div>
        <Button asChild size="sm" variant="ghost" className="-ml-2 gap-1.5 text-muted-foreground">
          <Link to="/orgs/$orgId/connections" params={{ orgId }}>
            <ArrowLeft className="size-4" />
            Connections
          </Link>
        </Button>
      </div>
      {!def || !status ? (
        <div className="rounded-lg border p-6 text-center text-sm text-muted-foreground">
          There&apos;s no workspace connection here.
        </div>
      ) : (
        <>
          <ConnectionHeader def={def} status={<StatusLine status={status} className="h-5 text-sm" />} />
          {def.id === "slack" ? (
            <WorkspaceSlack orgId={orgId} />
          ) : def.id === "github" || def.id === "gitlab" ? (
            <WorkspaceCodeHostsSection host={def.id} />
          ) : (
            platform && <SharedChatSection def={def} platform={platform} orgId={orgId} />
          )}
        </>
      )}
    </div>
  );
}

// Teams, Google Chat, Discord: installed from a Boo, where you pick the Boo
// that answers. The workspace page says which one does and links to it.
function SharedChatSection({ def, platform, orgId }: { def: ConnectionDef; platform: ChatPlatform; orgId: string }) {
  const query = useChatInstall(platform);
  const d = query.data;
  if (query.isLoading) return <LoadingIndicator message={`Checking ${def.name}…`} />;
  if (query.isError || !d) return <InlineError error={query.error ?? `Failed to check ${def.name}`} />;
  if (!d.installed && !d.workspaceConnected) {
    return (
      <Notice>
        <p>Add {def.name} from a Boo&apos;s Connections, where you pick the Boo that answers.</p>
      </Notice>
    );
  }
  const { place, placeNoun } = BOO_APPS[platform];
  return (
    <DetailList>
      <DetailRow term="Installed in">{place(d) ?? `Your ${placeNoun}`}</DetailRow>
      <DetailRow
        term="Answers through"
        action={
          d.containerId && (
            <Button asChild size="sm" variant="outline">
              <Link
                to="/orgs/$orgId/containers/$containerId/connections/$connection"
                params={{ orgId, containerId: d.containerId, connection: def.id }}
              >
                Open
              </Link>
            </Button>
          )
        }
      >
        {d.containerId ? (d.containerName ?? "A Boo") : "No Boo yet. Turn it on from the Connections of the Boo that should answer."}
      </DetailRow>
    </DetailList>
  );
}
